import { useEffect, useMemo, useState } from 'react'
import { css } from '@emotion/css'
import Typography from '@mui/material/Typography'
import theme from '@mike/mike-shared-frontend/mike-shared-styles/mikeSharedTheme'
import SiteInfoNav from '../SiteInfoNav'
import { useTypedSelector } from '../../reducers'
import { styled } from '@mui/material/styles'
import DataTable from '../DataTable'
import { useIntl } from 'react-intl'
import { StateEnum } from '../../model/StateEnum'
import { SiteCpuUsageState } from '../../reducers/siteCpuUsage'
import { useDispatch } from 'react-redux'
import { loadSiteCpuUsage } from '../../actions/siteCpuUsage'
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker'
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { MikeTooltip } from '@mike/mike-shared-frontend'
import moment, { Moment } from 'moment'
import { DATE_FORMAT, formatLocalDateTime } from '../../utils/dateFormat'
import LongName from '../DataTable/renders/LongName'
import { loadProjectNames } from '../../actions/projectNames'
import { useTablePageParam } from '../../hooks/use-table-page-param.hook'
import { useMeasuredHeight } from '../../hooks/use-measured-height.hook'

const paddingStyle = css`
  padding: ${theme.spacing(1)};
`

const StyledTableTopHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(4),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  paddingBottom: theme.spacing(2),
  zIndex: 1,
  backgroundColor: '#f2f5f7'
}))

const StyledDateRangePicker = styled('div')(({ theme }) => ({
  paddingLeft: theme.spacing(4),
  paddingBottom: theme.spacing(4)
}))

const hoverStyle = () => css`
  &:hover {
    cursor: pointer;
  }
`

const SiteCpuUsage = props => {
  const intl = useIntl()
  const tenantId = useTypedSelector(state => state.auth?.user?.tenantId)
  const siteCpuUsage = useTypedSelector<SiteCpuUsageState>(
    state => state.siteCpuUsage as SiteCpuUsageState
  )
  const dispatch = useDispatch()
  const projectNames = useTypedSelector(state => state.projectNames)
  const [urlPage, setUrlPage] = useTablePageParam()
  const [savedRowsPerPage] = useState<number>(() => {
    const raw = localStorage.getItem('site-cpu-rowsPerPage')
    return raw
      ? parseInt(raw, 10) || siteCpuUsage.params.rowsPerPage
      : siteCpuUsage.params.rowsPerPage
  })

  const [savedSort] = useState<{ orderBy: string; sortOrder: string }>(() => {
    try {
      const raw = localStorage.getItem('site-cpu-sort')
      if (raw) return JSON.parse(raw)
    } catch {
      // ignore
    }
    return {
      orderBy: siteCpuUsage.params.orderBy,
      sortOrder: siteCpuUsage.params.sortOrder
    }
  })

  useEffect(() => {
    if (tenantId && siteCpuUsage.state === StateEnum.INIT) {
      dispatch(
        loadSiteCpuUsage(tenantId, {
          ...siteCpuUsage.params,
          page: urlPage,
          rowsPerPage: savedRowsPerPage,
          orderBy: savedSort.orderBy,
          sortOrder: savedSort.sortOrder
        })
      )
    }
    if (tenantId && projectNames.state === StateEnum.INIT) {
      dispatch(loadProjectNames(tenantId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, siteCpuUsage, dispatch, projectNames.state])

  const [topOffset, measuredRef] = useMeasuredHeight()

  const projectNameRender = value => {
    if (projectNames.projects[value]) {
      return <LongName longName={projectNames.projects[value]} />
    }
    return (
      <MikeTooltip
        title={intl.formatMessage({
          id: 'screens.siteCpuUsage.unknownProject'
        })}
      >
        <div>{value}</div>
      </MikeTooltip>
    )
  }

  const projectTimeRender = value => {
    return <div>{value && formatLocalDateTime(value)}</div>
  }

  const projectMinutesRender = value => {
    return (
      <MikeTooltip title={value}>
        <span>{value.toFixed(2)}</span>
      </MikeTooltip>
    )
  }

  const onHandleRequestSort = (orderBy: string, sortOrder: string) => {
    localStorage.setItem(
      'site-cpu-sort',
      JSON.stringify({ orderBy, sortOrder })
    )
    dispatch(
      loadSiteCpuUsage(tenantId, {
        ...siteCpuUsage.params,
        page: 0,
        orderBy,
        sortOrder
      })
    )
  }

  const onChangePage = page => {
    setUrlPage(page)
    dispatch(loadSiteCpuUsage(tenantId, { ...siteCpuUsage.params, page }))
  }

  const onChangeRowsPerPage = rowsPerPage => {
    localStorage.setItem('site-cpu-rowsPerPage', String(rowsPerPage))
    setUrlPage(0)
    dispatch(
      loadSiteCpuUsage(tenantId, {
        ...siteCpuUsage.params,
        page: 0,
        rowsPerPage
      })
    )
  }

  useEffect(() => {
    if (siteCpuUsage.totalCount >= 0 && siteCpuUsage.params.page > 0) {
      const totalPages = Math.ceil(
        siteCpuUsage.totalCount / siteCpuUsage.params.rowsPerPage
      )
      if (siteCpuUsage.params.page >= totalPages) onChangePage(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteCpuUsage.totalCount])

  const setDateRange = (range: [Moment | null, Moment | null]) => {
    const [rangeStart, rangeEnd] = range
    if (!rangeStart || !rangeEnd) return
    const start = rangeStart
      .clone()
      .startOf('day')
      .format()
    const end = rangeEnd
      .clone()
      .endOf('day')
      .format()
    dispatch(
      loadSiteCpuUsage(tenantId, {
        ...siteCpuUsage.params,
        page: 0,
        startDate: start,
        endDate: end
      })
    )
  }

  const startAsMoment = useMemo(() => moment(siteCpuUsage.params.startDate), [
    siteCpuUsage.params.startDate
  ])
  const endAsMoment = useMemo(() => moment(siteCpuUsage.params.endDate), [
    siteCpuUsage.params.endDate
  ])

  return (
    <>
      <SiteInfoNav />
      <StyledTableTopHeader ref={measuredRef}>
        <Typography className={paddingStyle} variant="h4" color="textPrimary">
          {intl.formatMessage({
            id: 'screens.siteCpuUsage.cloudCpuUsage'
          })}
        </Typography>
      </StyledTableTopHeader>

      <StyledDateRangePicker>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DateRangePicker
            value={[startAsMoment, endAsMoment]}
            onChange={setDateRange}
            maxDate={moment()}
            format={DATE_FORMAT}
          />
        </LocalizationProvider>
      </StyledDateRangePicker>

      <DataTable
        loading={siteCpuUsage.state === StateEnum.LOADING}
        columns={[
          {
            field: 'projectId',
            label: intl.formatMessage({ id: 'common.project' }),
            render: projectNameRender,
            className: hoverStyle
          },
          {
            field: 'validFrom',
            label: intl.formatMessage({ id: 'screens.siteCpuUsage.startTime' }),
            render: projectTimeRender,
            className: hoverStyle
          },
          {
            field: 'value',
            label: intl.formatMessage({ id: 'common.minutes' }),
            render: projectMinutesRender,
            className: hoverStyle
          }
        ]}
        notSortableColumns={['projectId']}
        idField={item => `${item.operationId}-${item.validFrom}`}
        data={siteCpuUsage.cpuUsage}
        onHandleRequestSort={onHandleRequestSort}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        hasSelection={false}
        topOffset={topOffset}
        totalCount={siteCpuUsage.totalCount}
        hasMorePages={true}
        page={siteCpuUsage.params.page}
        rowsPerPage={siteCpuUsage.params.rowsPerPage}
        _order={[siteCpuUsage.params.sortOrder]}
        _orderBy={[siteCpuUsage.params.orderBy]}
      />
    </>
  )
}

export default SiteCpuUsage
